import { getDeviceUsers, updateUserPermissions } from 'lib/firebase';
import { useEffect, useState } from 'react';
import { Device } from 'types';
import { StoredUser } from 'store/features/authentication';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import styles from './DeviceUsers.module.scss';
import { FirebaseUser } from 'types/User/firebaseTypes';
import { activateSnackbar } from 'store/features/ui';
import { useDispatch } from 'react-redux';
import { formatDistanceToNowStrict } from 'date-fns';
import { Checkbox } from '@mui/material';
import { useSelector } from 'react-redux';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
// import { useNavigate } from 'react-router-dom';

interface DeviceUsersProps {
    device: Device;
    user: StoredUser | null;
}

/** Shows list of end-users associated with the device (i.e., consumers who own the device/have the device on their tank). */
export const DeviceUsers = ({ device, user }: DeviceUsersProps) => {
    const dispatch = useDispatch();
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);
    // const navigate = useNavigate();
    const [userList, setUserList] = useState<FirebaseUser[]>([]);

    useEffect(() => {
        if (user?.protected && viewAsSupplier) {
            getDeviceUsers(device.deviceId, viewAsSupplier)
                .then((result) => {
                    if (result) {
                        setUserList(result);
                    }
                })
                .catch((error) => dispatch(activateSnackbar(error)));
        }
    }, [setUserList, user, device, dispatch, viewAsSupplier]);

    const columns: DataTableProps['columns'] = [
        {
            field: 'uid',
            headerName: 'User Id',
            flex: 2,
            valueGetter: (param) => param.row.info?.uid ?? '-',
            sortingOrder: ['asc', 'desc'],
            hideByDefault: true,
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            valueGetter: (param) => param.row.info?.name ?? '-',
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 2,
            valueGetter: (param) => param.row.info?.email ?? '-',
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'address',
            headerName: 'Address',
            flex: 4,
            valueGetter: (param) => param.row.info?.address ?? '-',
            sortingOrder: ['asc', 'desc'],
            hideByDefault: true,
        },
        {
            field: 'phone',
            headerName: 'Phone',
            flex: 2,
            valueGetter: (param) => param.row.info?.phone ?? '-',
            sortingOrder: ['asc', 'desc'],
            hideByDefault: true,
        },
        {
            field: 'lastLogin',
            headerName: 'Last App Login',
            flex: 2,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (param) =>
                param.row.info?.lastLoginApp
                    ? formatDistanceToNowStrict(param.row.info.lastLoginApp)
                    : '-',
        },
        {
            field: 'readOnly',
            headerName: 'Read Only',
            flex: 1,
            type: 'actions',
            sortingOrder: ['asc', 'desc'],
            maxWidth: 100,
            renderCell: (param) => (
                <Checkbox
                    checked={param.row.protected?.read_only}
                    onChange={(e) => {
                        if (user?.uid) {
                            updateUserPermissions(
                                user.uid, // currentUserId
                                param.row.info?.uid, // updatedUserId
                                e.target.checked, // readOnly
                                param.row.protected?.roleId // roleId
                            );
                            param.row.protected.read_only = e.target.checked;
                        }
                    }}
                />
            ),
        },
    ];

    return (
        <div data-testid="DeviceUsers" className={styles.container}>
            <DataTable
                id="deviceUsers"
                columns={columns}
                data={userList}
                dataFormatter={(userList) => userList}
                getRowId={(user) => user.info.uid}
                getRowCount={() => userList.length}
                disableSelectionOnClick
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                }}
                disableColumnMenu
                // onRowClick={(params) =>
                //     navigate(`/users/${params.row.info?.uid}`, {
                //         state: params.row,
                //     })
                // }
            />
        </div>
    );
};
